import type { UserInfo } from '@/types/auth';

/**
 * Props for UserProfile component
 */
interface UserProfileProps {
  user: UserInfo;
  onSignOut: () => Promise<void>;
}

/**
 * User profile component that displays authenticated user information
 */
export const UserProfile = ({ user, onSignOut }: UserProfileProps) => {
  const formatDate = (timestamp: number | undefined): string => {
    if (!timestamp) {
      return 'N/A';
    }
    return new Date(timestamp * 1000).toLocaleString();
  };

  return (
    <div className="card max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Welcome Back!</h1>
        <button onClick={onSignOut} className="btn btn-secondary">
          Sign Out
        </button>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <h2 className="text-sm font-medium text-gray-500 mb-1">Email Address</h2>
          <p className="text-lg font-semibold">{user.email}</p>
        </div>

        {user.name && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h2 className="text-sm font-medium text-gray-500 mb-1">Name</h2>
            <p className="text-lg font-semibold">{user.name}</p>
          </div>
        )}

        <div className="p-4 bg-gray-50 rounded-lg">
          <h2 className="text-sm font-medium text-gray-500 mb-1">User ID</h2>
          <p className="text-sm font-mono break-all">{user.sub}</p>
        </div>

        {user.iss && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h2 className="text-sm font-medium text-gray-500 mb-1">Issuer</h2>
            <p className="text-sm font-mono break-all">{user.iss}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {user.iat && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h2 className="text-sm font-medium text-gray-500 mb-1">Issued At</h2>
              <p className="text-sm">{formatDate(user.iat)}</p>
            </div>
          )}

          {user.exp && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h2 className="text-sm font-medium text-gray-500 mb-1">Expires At</h2>
              <p className="text-sm">{formatDate(user.exp)}</p>
            </div>
          )}
        </div>

        <details className="p-4 bg-gray-50 rounded-lg">
          <summary className="text-sm font-medium text-gray-700 cursor-pointer">
            View Full JWT Claims
          </summary>
          <pre className="mt-3 text-xs overflow-x-auto bg-white p-3 rounded border border-gray-200">
            {JSON.stringify(user, null, 2)}
          </pre>
        </details>
      </div>
    </div>
  );
};
